import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  ClockIcon,
  UserIcon,
  FunnelIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  CogIcon,
} from '@heroicons/react/24/outline'
import { adminApiService } from '../../../shared/services/adminApi'
import { AdminHeader } from '../../../shared/components'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { formatDateTime as formatLocaleDateTime } from '../../../shared/lib/format'

interface PlanHistoryItem {
  id: string
  user: {
    id: string
    email: string
    name?: string
  } | null
  oldPlan: string
  newPlan: string
  reason: string
  method: string
  changedAt: string
  changedBy: {
    id: string
    email: string
    name?: string
  } | null
  metadata: any
}

interface PaginationInfo {
  currentPage: number
  totalPages: number
  totalCount: number
  hasNext: boolean
  hasPrev: boolean
}

export default function PlanHistoryPage() {
  const [planHistory, setPlanHistory] = useState<PlanHistoryItem[]>([])
  const [pagination, setPagination] = useState<PaginationInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [showFilters, setShowFilters] = useState(false)

  // Filter states
  const [filters, setFilters] = useState({
    planType: '',
    changedBy: '',
    dateFrom: '',
    dateTo: '',
    method: '',
  })

  useEffect(() => {
    loadPlanHistory()
  }, [currentPage, filters])

  const loadPlanHistory = async () => {
    try {
      setLoading(true)
      const data = await adminApiService.getAllPlanHistory({
        page: currentPage,
        limit: 20,
        ...filters,
      })

      setPlanHistory(data.planHistory || [])
      setPagination(data.pagination)
    } catch (error) {
      console.error('Failed to load plan history:', error)
      toast.error('Failed to load plan history')
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }))
    setCurrentPage(1) // Reset to first page when filter changes
  }

  const clearFilters = () => {
    setFilters({
      planType: '',
      changedBy: '',
      dateFrom: '',
      dateTo: '',
      method: '',
    })
    setCurrentPage(1)
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  const formatDate = (dateString: string) => {
    return formatLocaleDateTime(dateString)
  }

  const getPlanColor = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'free':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      case 'growth':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      case 'scale':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
      case 'enterprise':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'admin_manual':
        return <CogIcon className="h-4 w-4" />
      case 'subscription':
        return <UserIcon className="h-4 w-4" />
      case 'upgrade':
        return <ArrowRightIcon className="h-4 w-4" />
      default:
        return <DocumentTextIcon className="h-4 w-4" />
    }
  }

  const getMethodDisplay = (method: string) => {
    switch (method) {
      case 'admin_manual':
        return 'Admin Change'
      case 'subscription':
        return 'Subscription'
      case 'upgrade':
        return 'User Upgrade'
      case 'downgrade':
        return 'User Downgrade'
      default:
        return method.charAt(0).toUpperCase() + method.slice(1)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader
        title="Plan Change History"
        subtitle="View all plan changes across all users"
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Filter Plan Changes
            </h2>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium dark:border-gray-600 ${
                showFilters
                  ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300'
                  : 'bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300'
              } hover:bg-gray-50 dark:hover:bg-gray-700`}
            >
              <FunnelIcon className="mr-2 h-5 w-5" />
              Filters
            </button>
          </div>

          {showFilters && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {/* Plan Type Filter */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Plan Type
                  </label>
                  <select
                    value={filters.planType}
                    onChange={e =>
                      handleFilterChange('planType', e.target.value)
                    }
                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Plans</option>
                    <option value="free">Free</option>
                    <option value="growth">Growth</option>
                    <option value="scale">Scale</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>

                {/* Method Filter */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Change Method
                  </label>
                  <select
                    value={filters.method}
                    onChange={e => handleFilterChange('method', e.target.value)}
                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">All Methods</option>
                    <option value="admin_manual">Admin Change</option>
                    <option value="subscription">Subscription</option>
                    <option value="upgrade">User Upgrade</option>
                    <option value="downgrade">User Downgrade</option>
                  </select>
                </div>

                {/* Date From */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={e =>
                      handleFilterChange('dateFrom', e.target.value)
                    }
                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={e => handleFilterChange('dateTo', e.target.value)}
                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Filter Actions */}
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {Object.values(filters).some(value => value !== '') && (
                    <span>Filters applied</span>
                  )}
                </div>
                <button
                  onClick={clearFilters}
                  className="text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200"
                >
                  Clear all filters
                </button>
              </div>
            </motion.div>
          )}
        </div>

        {/* History Table */}
        <div className="overflow-hidden bg-white shadow dark:bg-gray-800 sm:rounded-lg">
          {loading ? (
            <div className="p-8 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Loading plan history...
              </p>
            </div>
          ) : planHistory.length === 0 ? (
            <div className="p-8 text-center">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                No plan changes found
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Try adjusting your filter criteria.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Plan Change
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Changed By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Reason
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {planHistory.map((item, index) => (
                    <motion.tr
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        {item.user ? (
                          <div>
                            <Link
                              to={`/users/${item.user.id}`}
                              className="text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200"
                            >
                              {item.user.name || item.user.email.split('@')[0]}
                            </Link>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {item.user.email}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            User deleted
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getPlanColor(item.oldPlan)}`}
                          >
                            {item.oldPlan}
                          </span>
                          <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getPlanColor(item.newPlan)}`}
                          >
                            {item.newPlan}
                          </span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400">
                          {getMethodIcon(item.method)}
                          <span>{getMethodDisplay(item.method)}</span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {item.changedBy ? (
                          <div>
                            <div className="font-medium">
                              {item.changedBy.name ||
                                item.changedBy.email.split('@')[0]}
                            </div>
                            <div className="text-xs">
                              {item.changedBy.email}
                            </div>
                          </div>
                        ) : (
                          <span>System</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(item.changedAt)}
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="max-w-xs truncate text-sm text-gray-900 dark:text-white"
                          title={item.reason}
                        >
                          {item.reason}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing page {pagination.currentPage} of {pagination.totalPages}
              <span className="ml-2">
                ({pagination.totalCount} total changes)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.hasPrev}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Page {pagination.currentPage}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasNext}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
